/**
 * VendorPortalEventRegistrationService
 * Service for managing vendor event registrations.
 * Route: api/vendor-portal/event-registrations
 * Returns enriched EventVendorRegistrationModelView with event and vendor details.
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalEventRegistrationService
 * Handles vendor event registration operations for the vendor portal.
 * Vendors can view, confirm, or decline their event registrations.
 * Maps to /api/vendor-portal/event-registrations endpoint
 */
export class VendorPortalEventRegistrationService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalEventRegistrationService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalEventRegistrationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all event registrations for the current vendor
     * GET /api/vendor-portal/event-registrations
     * Returns enriched view models with event and vendor details
     */
    async getRegistrations() {
        const response = await this.getRegistrationsRaw();
        return response.data ?? [];
    }
    /**
     * Get all event registrations (raw response)
     */
    getRegistrationsRaw() {
        return this.get();
    }
    // ==================== GET BY ID ====================
    /**
     * Get a specific event registration by ID
     * GET /api/vendor-portal/event-registrations/{id}
     * Returns enriched view model with event and vendor details
     */
    async getRegistration(id) {
        const response = await this.getRegistrationRaw(id);
        if (!response.data) {
            throw new Error(`Registration with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get a specific registration (raw response)
     */
    getRegistrationRaw(id) {
        return this.get(String(id));
    }
    // ==================== CONFIRM ====================
    /**
     * Confirm availability for an event registration
     * POST /api/vendor-portal/event-registrations/{id}/confirm
     * Returns enriched view model after confirmation
     */
    async confirmRegistration(id) {
        const response = await this.confirmRegistrationRaw(id);
        if (!response.data) {
            throw new Error(`Failed to confirm registration with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Confirm registration (raw response)
     */
    confirmRegistrationRaw(id) {
        return this.post(`${id}/confirm`, {});
    }
    // ==================== DECLINE ====================
    /**
     * Decline an event registration with an optional reason
     * POST /api/vendor-portal/event-registrations/{id}/decline
     * Returns enriched view model after declining
     */
    async declineRegistration(id, request) {
        const response = await this.declineRegistrationRaw(id, request);
        if (!response.data) {
            throw new Error(`Failed to decline registration with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Decline registration (raw response)
     */
    declineRegistrationRaw(id, request) {
        return this.post(`${id}/decline`, request ?? {});
    }
    // ==================== HELPER METHODS ====================
    /**
     * Get pending registrations that need confirmation
     */
    async getPendingRegistrations() {
        const registrations = await this.getRegistrations();
        return registrations.filter(r => r.isPending);
    }
    /**
     * Get confirmed registrations
     */
    async getConfirmedRegistrations() {
        const registrations = await this.getRegistrations();
        return registrations.filter(r => r.isConfirmed);
    }
    /**
     * Get declined registrations
     */
    async getDeclinedRegistrations() {
        const registrations = await this.getRegistrations();
        return registrations.filter(r => r.isDeclined);
    }
}
VendorPortalEventRegistrationService.DEFAULT_SERVICE_PATH = 'events';
VendorPortalEventRegistrationService.BASE_PATH = 'api/vendor-portal/event-registrations';
//# sourceMappingURL=VendorPortalEventRegistrationService.js.map